import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { isObjectTableFilter } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { getFilteredColumnMinWidth } from 'components/Table/model/commands/helpers/getFilteredColumnMinWidth';
import { setTableColumnWidth } from 'components/Table/model/commands/setTableColumnWidth';
import { TABLE_COLUMN_MIN_FILTERED_WIDTH } from 'components/Table/tableSettings';
import { type FC, useEffect, useState } from 'react';
import { IntentFilter } from './IntentFilter';
import { SimpleFilterInput } from './SimpleFilterInput';
import { SimpleObjectFilter } from './SimpleObjectFilter';
import type { ChangeFilterEvent } from './types/ChangeFilterEvent';
import type { SimpleFilterField } from './types/SimpleFilterField';

interface Props {
  filterField: SimpleFilterField;
  onChangeFilter: (command: ChangeFilterEvent) => void;
  onSubmitFilters: () => void;
  allowIntentFilter?: boolean;
  columnKey: string;
  columnWidth: number;
}

export const SimpleFilter: FC<Props> = (props) => {
  const [focusOnNextRender, setFocusOnNextRender] = useState<boolean>(false);
  useEffect(() => {
    if (focusOnNextRender) {
      setFocusOnNextRender(false);
    }
  }, [focusOnNextRender]);

  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    onChangeIntentFilter: prop('onChangeIntentFilter'),
    intentFilters: prop('intentFilters'),
  });

  const handleButtonClick = () => {
    ctxProps.dispatch(setTableColumnWidth, {
      columnKey: props.columnKey,
      width: getFilteredColumnMinWidth(props.filterField.filter),
    });
    setFocusOnNextRender(true);
  };

  const filterHiddenButton =
    props.columnWidth < TABLE_COLUMN_MIN_FILTERED_WIDTH - 1 ? (
      <IconButton
        variant='light-outline'
        icon='fa-magnifying-glass'
        size='small'
        onClick={handleButtonClick}
      />
    ) : null;

  const intentFilter = props.allowIntentFilter ? (
    <IntentFilter
      className='flex-fixed mr-1'
      onChangeIntentFilter={ctxProps.onChangeIntentFilter!}
      column={props.columnKey}
    />
  ) : null;

  const renderObjectFilter = () =>
    ctxProps.intentFilters?.[props.filterField.column] ? (
      intentFilter
    ) : (
      <SimpleObjectFilter
        {...props}
        intentFilter={intentFilter}
        filterHiddenButton={filterHiddenButton}
        autoFocus={focusOnNextRender}
      />
    );

  return isObjectTableFilter(props.filterField.filter) ? (
    renderObjectFilter()
  ) : (
    <div className='flex'>
      {intentFilter}
      {!ctxProps.intentFilters?.[props.filterField.column] &&
        (filterHiddenButton || (
          <SimpleFilterInput {...props} autoFocus={focusOnNextRender} />
        ))}
    </div>
  );
};
